import React, { useState, useEffect } from "react";
import {
	Table,
	Button,
	Modal,
	Form,
	Row,
	Col,
	Card,
	Space,
	notification,
	Tag,
} from "antd";
import { AppstoreAddOutlined, EditOutlined } from "@ant-design/icons";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import servicesTeams from "../../services/serviceTeams";
import TeamComponent from "../../components/team";

const ServiceTeams = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form] = Form.useForm();
	const [currentPage, setCurrentPage] = useState(1);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(15);
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]); // Initial state is an empty array
	const [currentId, setCurrentId] = useState(null);
	const [teamIds, setTeamIds] = useState(null);

	// MODIFIED: This function now correctly handles the API response.
	const getServiceTeams = async (page = 1) => {
		setLoading(true);
		try {
			const response = await servicesTeams.getServiceTeams(page, itemsPerPage);
			// FIX: The array is in response.data
			setData(response.data);
			// FIX: Pagination details are in the root of the response object
			setTotalDataCount(response.total);
			setItemsPerPage(response.per_page);
			setCurrentPage(page);
		} catch (error) {
			notification.error({
				message: "Error",
				description: "Failed to fetch service teams.",
			});
		} finally {
			setLoading(false);
		}
	};

	// No changes needed below this line, but left for context
	useEffect(() => {
		getServiceTeams(1);
	}, []);

	const handleCancel = () => {
		setIsModalVisible(false);
		setEditing(null);
		form.resetFields();
	};

	const handleTeamChange = (values) => {
		form.setFieldValue("team_ids", values);
		setTeamIds(values);
	};

	const handleEdit = (record) => {
		setEditing(record);
		setIsModalVisible(true);
		setCurrentId(record.id);
		const teamIds = record.teams.map((team) => team.id);
		form.setFieldsValue({
			team_ids: teamIds,
		});
	};

	const onFinish = async (values) => {
		setLoading(true);
		try {
			const response = await servicesTeams.postAttachTeams(currentId, values);
			if (response.id) {
				setIsModalVisible(false);
				setEditing(null);
				form.resetFields();
				notification.success({
					message: "Teams Attached",
					description: "Teams have been successfully attached to the service.",
				});
				getServiceTeams(currentPage);
			}
		} catch (error) {
			notification.error({
				message: "Error",
				description: error.response.data.message,
			});
		} finally {
			setLoading(false);
		}
	};

	const columns = [
		{
			title: "Services",
			dataIndex: "name",
			key: "name",
			sorter: (a, b) => a.name.localeCompare(b.name),
		},
		{
			title: "Teams",
			dataIndex: "teams",
			key: "teams",
			render: (teams) => {
				return teams.length > 0 ? (
					teams.map((team) => (
						<Tag key={team.id} color='green'>
							{team.name}
						</Tag>
					))
				) : (
					<Tag color='red'>No Teams</Tag>
				);
			},
		},
		{
			title: "Actions",
			key: "actions",
			fixed: "right",
			width: 80,
			render: (text, record) => (
				<Space>
					<div onClick={() => handleEdit(record)} style={{ cursor: "pointer" }}>
						<EditOutlined />
					</div>
				</Space>
			),
		},
	];

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header icon={<AppstoreAddOutlined />} title='Service Teams' />
				</Col>
			</Row>

			<Card>
				<Table
					scroll={{ x: 1000 }}
					style={{ minHeight: "100vh" }}
					columns={columns}
					// The .map function will now work correctly
					dataSource={data.map((item) => ({ ...item, key: item.id }))}
					pagination={{
						total: totalDataCount,
						current: currentPage,
						pageSize: itemsPerPage,
						showSizeChanger: false,
						onChange: (page) => getServiceTeams(page),
					}}
					loading={loading}
				/>
			</Card>

			<Modal
				title={editing ? "Attach Teams to Service" : "Attach Teams to Service"}
				open={isModalVisible}
				onCancel={handleCancel}
				footer={null}
				width={650}
			>
				<Form
					form={form}
					onFinish={onFinish}
					layout='vertical'
					className='mt-20'
				>
					<Form.Item
						name='team_ids'
						label='Teams'
						rules={[
							{ required: true, message: "Please select at least one team" },
						]}
					>
						<TeamComponent
							multi={true}
							value={teamIds}
							onChange={handleTeamChange}
						/>
					</Form.Item>

					<Form.Item>
						<Button type='primary' htmlType='submit' loading={loading}>
							{editing ? "Save Changes" : "Attach Teams"}
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</DefaultLayout>
	);
};

export default ServiceTeams;
